import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, X, UserPlus, UserCheck, PowerOff, Power, ArrowRightLeft, Pencil } from "lucide-react";
import PromoteLeaderDialog from "@/components/PromoteLeaderDialog";
import AddPartnerDialog from "@/components/org/AddPartnerDialog";
import EditMemberDialog from "@/components/org/EditMemberDialog";
import { useAuth } from "@/lib/auth";

function MoveSelect({ u, leaders, onMoved }) {
  const opts = leaders.filter((l) => l.id !== u.id && l.id !== u.nearest_upline_leader_id);
  const move = async (to) => {
    try { await api.post(`/users/${to}/reassign-upline`, { new_upline_leader_id: to, user_ids: [u.id] }); toast.success(`${u.name} moved`); onMoved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not move"); }
  };
  return (
    <Select value="" onValueChange={move}>
      <SelectTrigger className="h-10 w-full sm:w-44" data-testid={`org-move-${u.id}`}><ArrowRightLeft className="w-4 h-4 mr-1" /><SelectValue placeholder="Move to…" /></SelectTrigger>
      <SelectContent>{opts.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function OrgMembers({ readOnly, leaders, onChange, tick }) {
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [rows, setRows] = useState(null);
  const [promoting, setPromoting] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [all, setAll] = useState([]);
  const { user } = useAuth();
  useEffect(() => { api.get("/org/users").then((r) => setAll(r.data)).catch(() => {}); }, [tick]);
  const teams = [...new Set([user?.upline_team, ...all.map((m) => m.upline_team)].filter(Boolean))].sort();
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/org/users", { params: { q: q.trim() || undefined, include_archived: archived } })
        .then((r) => setRows(r.data)).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, archived, tick]);
  const archive = async (u) => {
    const reason = window.prompt(`Archive ${u.name}? They can be restored later. Optional note:`, "");
    if (reason === null) return;
    try { await api.post(`/users/${u.id}/archive`, { confirm: true, reason }); toast.success(`${u.name} archived`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not archive"); }
  };
  const restore = async (u) => {
    try { await api.post(`/users/${u.id}/reactivate`); toast.success(`${u.name} restored`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not restore"); }
  };
  return (
    <Card className="p-4 sm:p-5 space-y-3" data-testid="org-members">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <h2 className="font-serif text-xl text-[#0A1F44]">People in your organisation</h2>
        {!readOnly && <Button onClick={() => setAdding(true)} className="h-10 rounded-full bg-[#0A1F44] text-white" data-testid="org-add-partner"><UserPlus className="w-4 h-4 mr-1" />Add partner</Button>}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <label className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="h-11 pl-9 pr-9 text-base sm:text-sm" data-testid="org-search" aria-label="Search people" />
          {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5" aria-label="Clear search" data-testid="org-search-clear"><X className="w-4 h-4 text-slate-500" /></button>}
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600 min-h-[44px]"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} data-testid="org-show-archived" />Show archived</label>
      </div>
      <div className="text-xs text-slate-500" data-testid="org-result-count">{rows ? `${rows.length} ${rows.length === 1 ? "person" : "people"}` : "Loading…"}</div>
      {rows && !rows.length && <p className="text-sm text-slate-500" data-testid="org-no-results">No one matches “{q}”. Try part of a first name, last name or email.</p>}
      <ul className="divide-y divide-slate-100">
        {(rows || []).map((u) => (
          <li key={u.id} className="py-3 flex flex-col lg:flex-row lg:items-center gap-2" data-testid={`org-member-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#0A1F44] break-words flex flex-wrap items-center gap-1.5">{u.name}
                <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{u.role}</span>
                {u.archived && <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded">archived</span>}
              </div>
              <div className="text-xs text-slate-500 break-all">{u.email} · {u.upline_team || "no team"} · upline {u.upline_name || "—"}</div>
            </div>
            {!readOnly && (
              <div className="flex flex-wrap gap-2">
                {!u.archived && u.role !== "platinum" && <Button size="sm" variant="outline" className="h-10" onClick={() => setEditing(u)} data-testid={`org-edit-${u.id}`}><Pencil className="w-4 h-4 mr-1" />Edit</Button>}
                {!u.archived && u.role === "partner" && <Button size="sm" variant="outline" className="h-10" onClick={() => setPromoting({ id: u.id, name: u.name, upline_team: u.upline_team, upline_name: u.upline_name })} data-testid={`org-promote-${u.id}`}><UserCheck className="w-4 h-4 mr-1" />Make Leader</Button>}
                {!u.archived && u.role !== "platinum" && <MoveSelect u={u} leaders={leaders} onMoved={onChange} />}
                {u.role === "partner" && !u.archived && <Button size="sm" variant="ghost" className="h-10" onClick={() => archive(u)} data-testid={`org-archive-${u.id}`}><PowerOff className="w-4 h-4 mr-1 text-[#8A6D1F]" />Archive</Button>}
                {u.archived && <Button size="sm" variant="ghost" className="h-10" onClick={() => restore(u)} data-testid={`org-restore-${u.id}`}><Power className="w-4 h-4 mr-1 text-[#1F5D34]" />Restore</Button>}
              </div>
            )}
          </li>
        ))}
      </ul>
      <PromoteLeaderDialog open={!!promoting} target={promoting} onClose={() => setPromoting(null)} onDone={() => { setPromoting(null); onChange(); }} />
      <AddPartnerDialog open={adding} leaders={leaders} onClose={() => setAdding(false)} onDone={() => { setAdding(false); onChange(); }} />
      <EditMemberDialog member={editing} members={all} leaders={leaders} teams={teams} onClose={() => setEditing(null)} onDone={() => { setEditing(null); onChange(); }} />
    </Card>
  );
}
